import { useState, useEffect, useCallback } from 'react';
import api, { API_HOST } from '../../utils/api';
import { useToast } from '../shared/Toast';
import AssetPreviewModal from '../shared/AssetPreviewModal';
import MarketingPublishModal from './MarketingPublishModal';
import {
  HiOutlineSparkles, HiOutlineEye, HiOutlineDownload, HiOutlineGlobeAlt,
  HiOutlineTrash, HiOutlineExternalLink, HiOutlineRefresh, HiOutlineAcademicCap,
  HiOutlineFilm
} from 'react-icons/hi';

const STATUS_BADGE = {
  generating: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

export default function MarketingStudio() {
  const { addToast } = useToast();
  const [assetTypes, setAssetTypes] = useState([]);
  const [assetType, setAssetType] = useState('');
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [generating, setGenerating] = useState(false);
  const [assets, setAssets] = useState([]);
  const [preview, setPreview] = useState(null);
  const [wp, setWp] = useState({ connected: null });
  const [publishTarget, setPublishTarget] = useState(null); // asset being published
  const [openVideoOnPublish, setOpenVideoOnPublish] = useState(false); // expand video panel

  const loadAssets = useCallback(() => {
    api.get('/marketing/assets').then(({ data }) => setAssets(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/marketing/asset-types').then(({ data }) => {
      setAssetTypes(data);
      if (data[0]) setAssetType(data[0].id);
    }).catch(() => {});
    api.get('/marketing/wordpress/status').then(({ data }) => setWp(data)).catch(() => setWp({ connected: false }));
    loadAssets();
  }, [loadAssets]);

  // Poll any generating assets
  useEffect(() => {
    const anyGenerating = assets.some(a => a.status === 'generating');
    if (!anyGenerating) return;
    const t = setInterval(async () => {
      const gens = assets.filter(a => a.status === 'generating');
      for (const a of gens) {
        try {
          const { data } = await api.get(`/marketing/assets/${a.id}/status`);
          if (data.status !== 'generating') loadAssets();
        } catch { /* ignore */ }
      }
    }, 4000);
    return () => clearInterval(t);
  }, [assets, loadAssets]);

  const currentType = assetTypes.find(t => t.id === assetType);

  const handleGenerate = async () => {
    if (!assetType || !title.trim()) { addToast('Enter a subject to write about', 'error'); return; }
    setGenerating(true);
    try {
      await api.post('/marketing/assets/generate', { assetType, title, brief });
      addToast(`Generation started — using the ${currentType?.skillLabel || 'content'} skill`);
      setTitle(''); setBrief('');
      loadAssets();
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to start generation', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (asset) => {
    if (!window.confirm('Delete this failed item?')) return;
    try { await api.delete(`/marketing/assets/${asset.id}`); loadAssets(); }
    catch (err) { addToast(err.response?.data?.error || 'Delete failed', 'error'); }
  };

  const typeMeta = (id) => assetTypes.find(t => t.id === id);
  const typeLabel = (id) => typeMeta(id)?.label || id;

  return (
    <div className="max-w-5xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Content Studio</h1>
      <p className="text-gray-500 mb-6">Generate V2 Cloud marketing content and publish it to WordPress.</p>

      {/* Generate panel */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content type</label>
            <select value={assetType} onChange={e => setAssetType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              {assetTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="What should this be about?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>

        {/* Which skill will be used (shown before generating) */}
        {currentType && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-medium">
              <HiOutlineAcademicCap className="w-4 h-4" /> Follows the “{currentType.skillLabel}” skill
            </span>
            <span className="text-gray-400">
              Publishes to WordPress as <code>{currentType.wpType}</code>
              {currentType.supportsVideo && ' · supports a hero video'}
              {currentType.supportsNav && ' · choose a nav section'}
            </span>
          </div>
        )}

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Brief / instructions <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea value={brief} onChange={e => setBrief(e.target.value)} rows={3}
            placeholder="Angle, key points, target audience, tone, keywords... anything to steer the content."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <button onClick={handleGenerate} disabled={generating || !title.trim()}
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-v2-blue text-white rounded-lg font-medium disabled:opacity-50">
          <HiOutlineSparkles className="w-5 h-5" /> {generating ? 'Starting...' : 'Generate'}
        </button>
        <span className="ml-4 text-xs">
          WordPress:{' '}
          {wp.connected === null ? <span className="text-gray-400">checking...</span>
            : wp.connected
              ? <span className="text-green-600">connected ({wp.url})</span>
              : <span className="text-red-500">not configured</span>}
        </span>
      </div>

      {/* Content list */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">Generated content</h2>
        <button onClick={loadAssets} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
          <HiOutlineRefresh className="w-4 h-4" /> Refresh
        </button>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
        {assets.length === 0 && <div className="p-8 text-center text-gray-400 text-sm">Nothing generated yet.</div>}
        {assets.map(a => (
          <div key={a.id} className="p-4 flex items-center gap-4">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-gray-900 truncate">{a.title || a.topic?.title || 'Untitled'}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {typeLabel(a.assetType)} · {new Date(a.createdAt).toLocaleString()}
                {a.wpStatus && <> · <span className="text-green-600">WP: {a.wpStatus}</span></>}
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${STATUS_BADGE[a.status] || 'bg-gray-100 text-gray-600'}`}>
              {a.status === 'generating' ? (a.progressStage || 'generating') : a.status}
            </span>
            {a.status === 'completed' && (
              <div className="flex items-center gap-2">
                <button onClick={() => setPreview(a)} title="Preview" className="p-2 text-gray-500 hover:text-v2-blue"><HiOutlineEye className="w-5 h-5" /></button>
                <a href={`${API_HOST}/api/marketing/assets/${a.id}/download`} title="Download" className="p-2 text-gray-500 hover:text-v2-blue"><HiOutlineDownload className="w-5 h-5" /></a>
                <button onClick={() => { if (wp.connected === false) { addToast('WordPress is not configured (see admin Settings)', 'error'); return; } setOpenVideoOnPublish(true); setPublishTarget(a); }} title="Add an AI video" className="p-2 text-gray-500 hover:text-v2-blue"><HiOutlineFilm className="w-5 h-5" /></button>
                {a.wpPostUrl
                  ? <a href={a.wpPostUrl} target="_blank" rel="noopener noreferrer" title="Open the WordPress draft" className="p-2 text-green-600 hover:text-green-700"><HiOutlineExternalLink className="w-5 h-5" /></a>
                  : <button onClick={() => { if (wp.connected === false) { addToast('WordPress is not configured (see admin Settings)', 'error'); return; } setOpenVideoOnPublish(false); setPublishTarget(a); }} title="Publish to WordPress (draft)" className="p-2 text-gray-500 hover:text-v2-blue"><HiOutlineGlobeAlt className="w-5 h-5" /></button>}
              </div>
            )}
            {a.status === 'failed' && (
              <button onClick={() => handleDelete(a)} title="Delete" className="p-2 text-red-400 hover:text-red-600"><HiOutlineTrash className="w-5 h-5" /></button>
            )}
          </div>
        ))}
      </div>

      {preview && (
        <AssetPreviewModal
          previewUrl={`${API_HOST}/api/marketing/assets/${preview.id}/preview`}
          downloadUrl={`${API_HOST}/api/marketing/assets/${preview.id}/download`}
          fileFormat={preview.fileFormat}
          assetLabel={typeLabel(preview.assetType)}
          onClose={() => setPreview(null)}
        />
      )}

      {publishTarget && (
        <MarketingPublishModal
          asset={publishTarget}
          meta={typeMeta(publishTarget.assetType)}
          initialVideoOpen={openVideoOnPublish}
          onClose={() => { setPublishTarget(null); setOpenVideoOnPublish(false); }}
          onPublished={(data) => setAssets(prev => prev.map(a => a.id === publishTarget.id ? { ...a, wpStatus: data.status, wpPostUrl: data.editLink || data.link } : a))}
        />
      )}
    </div>
  );
}
